import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { ColorRing } from "react-loader-spinner";
import axios from "../axios";
import { ThemeContext } from "../context/ThemeContext";
import { useBusinessNameContext } from "../context/BusinessName";
import { extractChatUuid, setStoredChatUuid } from "../chat/chatIds";
import Footer from "./Footer";
import { Outlet } from "react-router-dom";

const Layout = () => {
  const location = useLocation();
  const { theme, setTheme } = useContext(ThemeContext);
  const { setBusinessName } = useBusinessNameContext();
  const { t } = useTranslation();

  // Modal states
  const [showRoomNumberModal, setShowRoomNumberModal] = useState(false);
  const room_qr = localStorage.getItem("room_uuid_before_redirect");
  const region = process.env.REACT_APP_SERVER_REGION;

  // Form states
  const [inputRoomNumber, setInputRoomNumber] = useState("");
  const [roomModalError, setRoomModalError] = useState("");

  // Loading states
  const [submittingRoomNumber, setSubmittingRoomNumber] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);

  // Footer visibility — compute from path so chat never flashes the nav over the input
  const shouldHideFooter =
    location.pathname.includes("/chat") ||
    location.pathname.includes("/RestaurantSubMenu") ||
    location.pathname.includes("/PharmacySubMenu") ||
    location.pathname.includes("/FlowersSubMenu") ||
    location.pathname.includes("/SupermarketSubMenu") ||
    location.pathname.includes("/restaurant-menu/categories") ||
    location.pathname.includes("/restaurants/search") ||
    location.pathname.includes("/restaurant-menu") ||
    location.pathname.includes("/rstauran-menu-categories") ||
    location.pathname.includes("/rstauran-menu-subcategories") ||
    location.pathname.includes("/rstauran-menu-items") ||
    location.pathname.includes("/Checkout-dining") ||
    location.pathname.includes("/qr-scan") ||
    location.pathname.includes("/RedirectTaxi") ||
    location.pathname.includes("/scan-qr-code") ||
    location.pathname.includes("/not-found") ||
    location.pathname.includes("/food-and-beverages") ||
    location.pathname.includes("/cart") ||
    location.pathname.includes("/search") ||
    location.pathname.includes("/hotel-services") ||
    location.pathname.includes("/qrcode") ||
    location.pathname.includes("/restaurant/combo-meals") ||
    location.pathname.includes("/taxi-yango") ||
    location.search.includes("Restaurant&in_dining") ||
    Boolean(theme);

  // Get room_uuid from URL
  const rawPath = location.pathname;
  const room_uuid = rawPath.split("/")[1];

  const lang = sessionStorage.getItem("lang");

  // Initialize app data and check for modals
  useEffect(() => {
    const initializeApp = async () => {
      if (!room_uuid) return;

      setDataLoading(true);
      try {
        const res = await axios.get(`/${room_uuid}/footer/qr-code`, {
          headers: {
            lang: lang,
          },
        });
        const data = res.data.data;

        // Store data in localStorage
        const resolvedChatUuid = extractChatUuid(data);
        if (resolvedChatUuid) {
          setStoredChatUuid(room_uuid, resolvedChatUuid);
        }
        localStorage.setItem("room_number", data?.room_number);
        localStorage.setItem("currency", data?.currency || "AED");
        localStorage.setItem("color", data?.color);
        localStorage.setItem("tax_inclusive", data?.tax_inclusive);
        localStorage.setItem("show_prices", data?.show_prices);
        localStorage.setItem("has_extra_services", data?.has_extra_services);
        localStorage.setItem("new_design", data?.use_new_design);
        localStorage.setItem("businessType", data.businessType);
        localStorage.setItem("countryCode", data?.country_code || "ae");
        localStorage.setItem("business_name", data?.business_name);
        localStorage.setItem("city", data?.city);

        const sessionInfo = {
          "Room UUID": room_qr,
          "Session ID": room_uuid,
          "Business Name": data?.business_name,
          "Room Number": data?.room_number,
          "Location Type": data?.room_type,
          "Business ID": data?.business_id,
          "Server Region": region,
          Language: lang,
          Platform: "Web",
          utm_source: "",
          utm_medium: "",
          utm_campaign: "",
        };
        localStorage.setItem("mixpannel_data", JSON.stringify(sessionInfo));

        // Update context
        setTheme(data?.use_new_design);
        setBusinessName(data?.business_name);

        // Set CSS variables
        if (data?.color) {
          document.documentElement.style.setProperty("--color", data?.color);
          document.documentElement.style.setProperty(
            "--color_t",
            `${data?.color}b3`,
          );
          document.documentElement.style.setProperty(
            "--header_color",
            data?.header_color,
          );
        }

        if (data?.room_number_required) {
          setShowRoomNumberModal(true);
        }
      } catch (err) {
        console.error("Error initializing app data", err);
      } finally {
        setDataLoading(false);
      }
    };

    initializeApp();
  }, [room_uuid, setTheme, setBusinessName]);

  // Handle room number submission
  const handleRoomNumberSubmit = async () => {
    if (!inputRoomNumber) {
      setRoomModalError(t("Please enter a room number"));
      return;
    }

    setSubmittingRoomNumber(true);
    setRoomModalError("");

    try {
      const res = await axios.post(`/${room_uuid}/validate-room-number`, {
        room_number: inputRoomNumber,
      });

      if (res.data.data.room) {
        window.location.replace(`/${res.data.data.room}/qr-code`);
      } else {
        setRoomModalError(t("Invalid room number"));
        setSubmittingRoomNumber(false);
      }
    } catch (error) {
      console.error("Room validation error:", error);
      setRoomModalError(t("Failed to validate room number"));
      setSubmittingRoomNumber(false);
    }
  };

  return (
    <>
      <Outlet />
      {!shouldHideFooter && <Footer />}

      {/* Room Number Modal */}
      <Modal
        className={`x ${theme ? "food-modal" : ""}`}
        show={showRoomNumberModal}
        backdrop="static"
        keyboard={false}
        centered
      >
        <Modal.Body style={{ padding: "2rem", height: "100%" }}>
          <h2
            style={{
              textAlign: "center",
              color: theme ? "white" : "black",
            }}
          >
            {t("enter your room number")}
          </h2>
          <input
            type="text"
            className="form-control my-3"
            placeholder={t("room number")}
            value={inputRoomNumber}
            onChange={(e) => setInputRoomNumber(e.target.value)}
          />
          {roomModalError && (
            <p className="text-danger" style={{ textAlign: "center" }}>
              {roomModalError}
            </p>
          )}
          <div
            style={{
              textAlign: "center",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <button
              className="reg-btn"
              onClick={handleRoomNumberSubmit}
              disabled={submittingRoomNumber}
              style={{ opacity: submittingRoomNumber ? "0.7" : "1" }}
            >
              {submittingRoomNumber ? (
                <div
                  style={{
                    textAlign: "center",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <ColorRing
                    visible={true}
                    height="20"
                    width="20"
                    ariaLabel="color-ring-loading"
                    wrapperStyle={{
                      textAlign: "center",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    wrapperClass="color-ring-wrapper"
                    colors={["#000"]}
                  />
                </div>
              ) : (
                t("verify")
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Layout;
